import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Layers } from 'lucide-react';
import { API_URL } from '@/config/api';

interface PersonaBenefitMapping {
  benefitName?: string;
  personaName?: string;
  templateId?: string;
  priorityWeight: number;
  priorityRank: number;
  notes: string;
}

interface PersonaBenefitMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sectorId: number;
  mode: 'persona-to-benefits' | 'benefit-to-personas';
  // If mapping benefits to a persona:
  personaName?: string;
  personaDescription?: string;
  // If mapping personas to a benefit:
  benefitName?: string;
  benefitDescription?: string;
  // Callback after successful mapping
  onSuccess?: () => void;
}

interface BenefitOption {
  benefit_name: string;
  benefit_description?: string;
}

interface PersonaOption {
  persona_name: string;
  description?: string;
  age_range?: string;
  occupation?: string;
}

interface CategoryTemplate {
  id: string;
  category_name: string;
  category_path?: string;
  description?: string;
}

export function PersonaBenefitMappingDialog({
  open,
  onOpenChange,
  sectorId,
  mode,
  personaName,
  personaDescription,
  benefitName,
  benefitDescription,
  onSuccess,
}: PersonaBenefitMappingDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Category templates (new architecture)
  const [categoryTemplates, setCategoryTemplates] = useState<CategoryTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // Available options
  const [benefits, setBenefits] = useState<BenefitOption[]>([]);
  const [personas, setPersonas] = useState<PersonaOption[]>([]);

  // Selected mappings
  const [selectedMappings, setSelectedMappings] = useState<Map<string, PersonaBenefitMapping>>(new Map());

  useEffect(() => {
    if (open) {
      loadCategoryTemplates();
      // Reset selections when dialog opens
      setSelectedTemplate('');
      setBenefits([]);
      setPersonas([]);
      setSelectedMappings(new Map());
    }
  }, [open, sectorId]);

  useEffect(() => {
    if (selectedTemplate) {
      loadOptionsForTemplate();
    } else {
      setBenefits([]);
      setPersonas([]);
    }
  }, [selectedTemplate, mode]);

  const loadCategoryTemplates = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/category-templates?sectorId=${sectorId}`);
      const data = await response.json();
      if (data.success) {
        setCategoryTemplates(data.data.templates || []);
      }
    } catch (error) {
      console.error('Error loading category templates:', error);
      toast({
        title: 'Error',
        description: 'Failed to load category templates',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadOptionsForTemplate = async () => {
    if (!selectedTemplate) return;

    setLoading(true);
    try {
      if (mode === 'persona-to-benefits') {
        // Load benefits for the selected category template
        const response = await authFetch(
          `${API_URL}/category-templates/${selectedTemplate}/benefits`
        );
        const data = await response.json();
        if (data.success) {
          setBenefits(
            (data.data.benefits || []).map((b: any) => ({
              benefit_name: b.benefit_name,
              benefit_description: b.benefit_description,
            }))
          );
        }
      } else {
        // Load personas for this sector (personas are sector-level, not category-specific)
        const response = await authFetch(`${API_URL}/personas?sectorId=${sectorId}`);
        const data = await response.json();
        setPersonas(data.data || []);
      }
    } catch (error) {
      console.error('Error loading options for template:', error);
      toast({
        title: 'Error',
        description: 'Failed to load available options',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelection = (key: string) => {
    const newMappings = new Map(selectedMappings);
    if (newMappings.has(key)) {
      newMappings.delete(key);
    } else {
      // Initialize with default values
      newMappings.set(key, {
        benefitName: mode === 'benefit-to-personas' ? benefitName : key,
        personaName: mode === 'persona-to-benefits' ? personaName : key,
        templateId: selectedTemplate,
        priorityWeight: 0.5,
        priorityRank: 5,
        notes: '',
      });
    }
    setSelectedMappings(newMappings);
  };

  const updateMapping = (key: string, updates: Partial<PersonaBenefitMapping>) => {
    const newMappings = new Map(selectedMappings);
    const existing = newMappings.get(key);
    if (existing) {
      newMappings.set(key, { ...existing, ...updates });
      setSelectedMappings(newMappings);
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) {
      toast({
        title: 'No category selected',
        description: 'Please select a product category first',
        variant: 'destructive',
      });
      return;
    }

    if (selectedMappings.size === 0) {
      toast({
        title: 'No selections',
        description: 'Please select at least one item to map',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      // Create individual mappings using the new category-based architecture
      const results = await Promise.allSettled(
        Array.from(selectedMappings.values()).map(async (mapping) => {
          const response = await authFetch(`${API_URL}/persona-template-benefit-mappings`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              sectorId,
              personaName: mapping.personaName,
              templateId: mapping.templateId,
              benefitName: mapping.benefitName,
              priorityWeight: mapping.priorityWeight,
              priorityRank: mapping.priorityRank,
              notes: mapping.notes,
            }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to create mapping');
          }

          return response.json();
        })
      );

      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      toast({
        title: 'Success',
        description: `Created ${successful} mapping(s)${failed > 0 ? ` (${failed} failed)` : ''}`,
      });

      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Error creating mappings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create mappings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const renderOptions = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      );
    }

    const items = mode === 'persona-to-benefits' ? benefits : personas;

    if (items.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          No {mode === 'persona-to-benefits' ? 'benefits' : 'personas'} available
        </div>
      );
    }

    return (
      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        {items.map((item) => {
          const key = mode === 'persona-to-benefits'
            ? (item as BenefitOption).benefit_name
            : (item as PersonaOption).persona_name;
          const isSelected = selectedMappings.has(key);
          const mapping = selectedMappings.get(key);

          return (
            <div key={key} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id={`select-${key}`}
                  checked={isSelected}
                  onCheckedChange={() => handleToggleSelection(key)}
                />
                <div className="flex-1">
                  <Label
                    htmlFor={`select-${key}`}
                    className="text-base font-medium cursor-pointer"
                  >
                    {key}
                  </Label>
                  {mode === 'benefit-to-personas' && (item as PersonaOption).description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {(item as PersonaOption).description}
                    </p>
                  )}
                  {mode === 'persona-to-benefits' && (item as BenefitOption).benefit_description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {(item as BenefitOption).benefit_description}
                    </p>
                  )}
                </div>
              </div>

              {isSelected && mapping && (
                <div className="ml-7 space-y-3 pt-2 border-t">
                  <div>
                    <Label className="text-sm">
                      Priority Weight: {mapping.priorityWeight.toFixed(2)}
                    </Label>
                    <Slider
                      value={[mapping.priorityWeight]}
                      onValueChange={([value]) =>
                        updateMapping(key, { priorityWeight: value })
                      }
                      min={0}
                      max={1}
                      step={0.05}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      0 = lowest priority, 1 = highest priority
                    </p>
                  </div>

                  <div>
                    <Label className="text-sm">
                      Priority Rank: {mapping.priorityRank}
                    </Label>
                    <Slider
                      value={[mapping.priorityRank]}
                      onValueChange={([value]) =>
                        updateMapping(key, { priorityRank: value })
                      }
                      min={1}
                      max={10}
                      step={1}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      1 = most important, 10 = least important
                    </p>
                  </div>

                  <div>
                    <Label htmlFor={`notes-${key}`} className="text-sm">
                      Notes (optional)
                    </Label>
                    <Textarea
                      id={`notes-${key}`}
                      value={mapping.notes}
                      onChange={(e) =>
                        updateMapping(key, { notes: e.target.value })
                      }
                      placeholder="Why is this priority important for this persona/benefit?"
                      className="mt-2"
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {mode === 'persona-to-benefits'
              ? `Map Category Benefits to "${personaName}"`
              : `Map Personas to Category Benefit`}
          </DialogTitle>
          <DialogDescription>
            {mode === 'persona-to-benefits' ? (
              <>
                First select a product category, then choose which category-specific benefits are important for{' '}
                <strong>{personaName}</strong>
                {personaDescription && ` (${personaDescription})`}.
              </>
            ) : (
              <>
                First select a product category, then choose which personas value category-specific benefits.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Category Template Selector */}
        <div className="border-b pb-4">
          <Label htmlFor="category-select" className="flex items-center gap-2 mb-2">
            <Layers className="h-4 w-4" />
            Product Category
          </Label>
          <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
            <SelectTrigger id="category-select">
              <SelectValue placeholder="Select a product category..." />
            </SelectTrigger>
            <SelectContent>
              {categoryTemplates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  <div className="flex flex-col">
                    <span className="font-medium">{template.category_name}</span>
                    {template.category_path && (
                      <span className="text-xs text-muted-foreground">{template.category_path}</span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedTemplate && (
            <p className="text-xs text-muted-foreground mt-2">
              Category selected. Now choose {mode === 'persona-to-benefits' ? 'benefits' : 'personas'} below.
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto py-4">
          {!selectedTemplate ? (
            <div className="text-center py-8 text-muted-foreground">
              <Layers className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Please select a product category to continue</p>
            </div>
          ) : (
            renderOptions()
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || selectedMappings.size === 0}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              `Save ${selectedMappings.size} Mapping${selectedMappings.size !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
